import React, { useRef, useState, useEffect } from 'react';
import { Loading } from 'carbon-components-react';
import { loadModules } from 'esri-loader';
import { useSelector } from 'react-redux';
import { useComponentMount } from 'utilities/hooks.util';
import { selectBaseMap } from 'redux/app/selectors';
import { MAP_DEFAULT_VAR } from 'types';
import { findBasemapByIdentifier } from 'utilities/basemap-migration.util';
import { debugLog, debugWarn } from 'utilities/basemap-debug.util';

import Settings from '../Map/settings';
import { zoomToFeature, calculateArea } from './helper';

const PRESERVED_LAYER_IDS = ['current-enquiry-layer', 'current-referral-layer'];

const restoreViewToEnquiryShape = (mapObject: React.MutableRefObject<any>) => {
  const { loadedMap, loadedView } = mapObject?.current || {};
  if (!loadedMap || !loadedView || loadedView.destroyed) {
    return;
  }

  const zoomToLayerGraphic = () => {
    for (const layerId of PRESERVED_LAYER_IDS) {
      const layer = loadedMap.findLayerById(layerId);
      const graphic = layer?.graphics?.getItemAt?.(0);
      const graphicExtent = graphic?.geometry?.extent;
      if (graphicExtent) {
        const area = calculateArea(mapObject.current, graphic.geometry);
        const bufferSize = area > 0 ? Math.sqrt(area) * 0.2 : 0.2;
        zoomToFeature(mapObject.current, graphicExtent, bufferSize);
        mapObject.current.mapOriginalExtent = graphicExtent;
        return true;
      }
    }

    if (mapObject.current?.shapeZoomExtent) {
      const { extent, bufferSize } = mapObject.current.shapeZoomExtent;
      zoomToFeature(mapObject.current, extent, bufferSize);
      return true;
    }

    return false;
  };

  loadedView
    .when(() => {
      for (const layerId of PRESERVED_LAYER_IDS) {
        const layer = loadedMap.findLayerById(layerId);
        if (layer) {
          loadedView
            .whenLayerView(layer)
            .then(() => zoomToLayerGraphic())
            .catch(() => zoomToLayerGraphic());
          return;
        }
      }

      zoomToLayerGraphic();
    })
    .catch(() => zoomToLayerGraphic());
};

const StaticMap: React.FC<Props> = ({ mapObject, onReady, onZoomChange }) => {
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDualLoading, setIsDualLoading] = useState(false);
  const baseMap = useSelector(selectBaseMap);
  const basemapOptions = useSelector(
    (state: any) => state.mapCache?.basemapOptions
  );
  const initialBasemapAppliedRef = useRef(false);
  const isMountedRef = useRef(true);
  const onReadyRef = useRef(onReady);
  const onZoomChangeRef = useRef(onZoomChange);

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onReadyRef.current = onReady;
    onZoomChangeRef.current = onZoomChange;
  });

  const { modules, AUS_Extent, wkid } = Settings;
  const {
    map,
    mapView,
    extent,
    spatialReference,
    graphic,
    graphicsLayer,
    featureLayer,
    geometryEngine,
    webMercatorUtils,
    config,
    basemap,
    basemapStyle
  } = modules;
  const modulesPrepareToLoad = [
    map,
    mapView,
    extent,
    spatialReference,
    graphic,
    graphicsLayer,
    featureLayer,
    webMercatorUtils,
    geometryEngine,
    config,
    basemap,
    basemapStyle
  ];
  const moduleNamespace = modulesPrepareToLoad.map((m) => m.namespace);
  const moduleKey = modulesPrepareToLoad.map((m) => m.key);

  /**
   * Apply persisted basemap from Redux after map loads and basemapOptions are available
   * This handles the race condition where the map initializes before Redux rehydrates
   * or before basemapOptions are loaded from the feature service
   */
  useEffect(() => {
    // Skip if:
    // - Map not loaded yet
    // - No baseMap value from Redux
    // - Already applied initial basemap
    // - BasemapOptions not loaded yet (need them to look up config)
    if (
      !isLoaded ||
      !baseMap ||
      initialBasemapAppliedRef.current ||
      !basemapOptions ||
      basemapOptions.length === 0
    ) {
      return;
    }

    const loadedView = mapObject?.current?.loadedView;
    const loadedMap = mapObject?.current?.loadedMap;
    if (!loadedView || !loadedMap) {
      return;
    }

    // Mark as applied to prevent re-running
    initialBasemapAppliedRef.current = true;

    // Load required ArcGIS modules
    loadModules([
      'esri/Basemap',
      'esri/support/BasemapStyle',
      'esri/WebMap'
    ]).then(([Basemap, BasemapStyle, WebMap]: any[]) => {
      // Check if component is still mounted
      if (!isMountedRef.current) return;

      // Find basemap config
      const basemapItem = findBasemapByIdentifier(baseMap, basemapOptions);
      if (!basemapItem) {
        debugWarn(
          '[StaticMap Update] No basemap config found for:',
          baseMap,
          '- keeping current basemap'
        );
        return;
      }

      const config = {
        name: basemapItem.label,
        styleId: basemapItem.styleId,
        webmapId: basemapItem.webmapId
      };

      debugLog('[StaticMap Update] Applying persisted basemap from Redux:', {
        id: baseMap,
        name: config.name,
        styleId: config.styleId,
        webmapId: config.webmapId
      });

      // Handle dual loading (style + webmap)
      if (config.styleId && config.webmapId) {
        debugLog(
          '[StaticMap Update] Dual loading: WebMap + BasemapStyle override'
        );
        // Hide the map until the WebMap (with operational layers) is fully ready
        setIsDualLoading(true);
        const webMapInstance = new WebMap({
          portalItem: { id: config.webmapId }
        });
        webMapInstance
          .loadAll()
          .then(() => {
            // Check if component is still mounted before updating state
            if (!isMountedRef.current) return;

            debugLog('[StaticMap Update] WebMap loaded successfully');
            webMapInstance.basemap = new Basemap({
              style: new BasemapStyle({ id: config.styleId })
            });
            // Preserve operational layers from the previous map (e.g., mini-map polygon layer)
            try {
              const prevMap: any = loadedMap;
              if (prevMap && typeof prevMap.findLayerById === 'function') {
                PRESERVED_LAYER_IDS.forEach((layerId) => {
                  const layer = prevMap.findLayerById(layerId);
                  if (layer) {
                    webMapInstance.add(layer);
                  }
                });
              }
            } catch (e) {
              debugWarn(
                '[StaticMap Update] Failed to preserve layers during dual load:',
                e
              );
            }

            // Swap the view's map to the fully loaded WebMap
            loadedView.map = webMapInstance;

            // Update references so downstream code uses the new map instance
            if (mapObject && mapObject.current) {
              mapObject.current.loadedMap = webMapInstance;
            }

            restoreViewToEnquiryShape(mapObject);

            if (typeof onReadyRef.current === 'function') {
              onReadyRef.current();
            }

            debugLog('[StaticMap Update] Dual loading complete');
            setIsDualLoading(false);
          })
          .catch((error: any) => {
            // Check if component is still mounted before updating state
            if (!isMountedRef.current) return;
            debugWarn('[StaticMap Update] WebMap load failed:', error);
            setIsDualLoading(false);
          });
      }
      // Handle style-only basemaps
      else if (config.styleId) {
        debugLog('[StaticMap Update] Using BasemapStyle:', config.styleId);
        loadedMap.basemap = new Basemap({
          style: new BasemapStyle({ id: config.styleId })
        });
      }
      // Handle webmap-only basemaps (state-specific basemaps)
      else if (config.webmapId) {
        debugLog(
          '[StaticMap Update] Using Basemap with portalItem:',
          config.webmapId
        );
        const webmapBasemap = new Basemap({
          portalItem: { id: config.webmapId }
        });
        webmapBasemap
          .load()
          .then(() => {
            // Check if component is still mounted
            if (!isMountedRef.current) return;
            debugLog('[StaticMap Update] Webmap basemap loaded successfully');
            loadedMap.basemap = webmapBasemap;
          })
          .catch((error: any) => {
            // Check if component is still mounted
            if (!isMountedRef.current) return;
            debugWarn('[StaticMap Update] Webmap basemap load failed:', error);
            // Still try to set it
            loadedMap.basemap = webmapBasemap;
          });
      }
    });
  }, [isLoaded, baseMap, basemapOptions, mapObject]);

  useComponentMount(() => {
    loadModules(moduleNamespace, { css: true })
      .then((loadedModules: any[]) => {
        // Check if component is still mounted
        if (!isMountedRef.current) return;

        const mapModules: any = {};
        loadedModules.forEach((m, inx) => {
          mapModules[moduleKey[inx]] = m;
        });
        const {
          Extent,
          SpatialReference,
          Map,
          MapView,
          Config,
          Basemap,
          BasemapStyle
        } = mapModules;

        // Always set API key - interceptors will replace it with session token for basemap requests
        Config.apiKey = process.env.REACT_APP_ESRI_API_KEY;

        const extent = new Extent({
          ...AUS_Extent,
          spatialReference: new SpatialReference({ wkid })
        });

        // Create basemap using dual loading pattern (style + optional webmap)
        const basemapId = baseMap || MAP_DEFAULT_VAR.BASEMAP;

        // Find basemap config from Redux state (feature service data)
        const basemapItem = findBasemapByIdentifier(basemapId, basemapOptions);
        var matchedConfig = null;

        if (basemapItem) {
          matchedConfig = {
            name: basemapItem.label,
            styleId: basemapItem.styleId,
            webmapId: basemapItem.webmapId
          };
        } else {
          // Fallback: Use static mapping for common basemaps if options not loaded yet
          const staticBasemapConfigs: {
            [key: string]: { styleId: string; webmapId?: string };
          } = {
            streets: { styleId: 'arcgis/streets' },
            imagery: { styleId: 'arcgis/imagery' },
            imageryHybrid: { styleId: 'arcgis/imagery/labels' },
            openStreetMap: { styleId: 'osm/standard' }
          };
          if (staticBasemapConfigs[basemapId]) {
            matchedConfig = staticBasemapConfigs[basemapId];
          }
        }

        // Dual loading logic: Use styleId if available, fallback to default
        var basemapToUse = null;
        if (matchedConfig) {
          if (matchedConfig.styleId) {
            // Use BasemapStyle for style-based basemaps
            basemapToUse = new Basemap({
              style: new BasemapStyle({ id: matchedConfig.styleId })
            });
          } else if (matchedConfig.webmapId) {
            // Use Basemap with portalItem for webmap-only basemaps (e.g., state basemaps)
            basemapToUse = new Basemap({
              portalItem: {
                id: matchedConfig.webmapId
              }
            });
          }
        } else {
          // No config found - use default imagery basemap style
          // Fallback to default imagery style
          basemapToUse = new Basemap({
            style: new BasemapStyle({ id: 'arcgis/imagery' })
          });
        }

        const map = new Map({
          basemap: basemapToUse
        });

        const view = new MapView({
          container: mapDivRef.current,
          map,
          extent: extent,
          constraints: {
            maxZoom: 22,
            geometry: new Extent(Settings.AUS_Boundary_Constraint),
            minScale: Settings.mapMinScale
          }
        });

        view.ui.padding = 0;
        view.ui.remove('zoom');
        view.ui.remove('attribution');
        view.navigation.mouseWheelZoomEnabled = false;
        view.navigation.momentumEnabled = false;
        view.navigation.browserTouchPanEnabled = false;
        view.navigation.gamepad.enabled = false;

        view.on('drag', (event: any) => {
          event.stopPropagation();
        });

        view.on('key-down', (event: any) => {
          event.stopPropagation();
        });

        view.on('double-click', (event: any) => {
          event.stopPropagation();
        });

        if (!isLoaded) {
          mapObject.current.loadedMap = map;
          mapObject.current.loadedView = view;
          mapObject.current.loadedMapModules = mapModules;
          if (!mapObject.current.mapOriginalExtent) {
            mapObject.current.mapOriginalExtent = extent;
          }

          // Wait for view to be fully ready in ArcGIS SDK 4.33+ before calling onReady
          view
            .when(() => {
              // Check if component is still mounted before updating state
              if (!isMountedRef.current) return;

              if (typeof onZoomChangeRef.current === 'function') {
                onZoomChangeRef.current(view.zoom);
              }

              // Only call onReady after view is fully initialized
              if (typeof onReadyRef.current === 'function') {
                onReadyRef.current();
              }

              setIsLoaded(true);
            })
            .catch((error: any) => {
              // Check if component is still mounted before updating state
              if (!isMountedRef.current) return;
              debugWarn('Error initializing map view:', error);
              setIsLoaded(true);
            });
        } else {
          setIsLoaded(true);
        }
      })
      .catch(() => {});
  });

  return (
    <div className={`map-container ${isLoaded ? '--loaded' : ''}`}>
      <div
        className="map"
        ref={mapDivRef}
        data-testid="map"
        style={{ visibility: isDualLoading ? 'hidden' : 'visible' }}
      />
      {(!isLoaded || isDualLoading) && (
        <div className="map-loader">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      )}
    </div>
  );
};

type Props = {
  mapObject: React.MutableRefObject<any>;
  onReady: Function;
  onZoomChange?: Function;
};

export default React.memo(StaticMap);
